<template>
  <div>
    <!-- BEGIN PAGE TITLE-->
    <h1 class="page-title">
      Donation Campaigns
    </h1>
    <!-- END PAGE TITLE -->
    <div class="note note-info">
      <p>Add and manage donation campaigns.</p>
    </div>

    <!-- BEGIN CREATE -->
    <div
      v-if="can('donation create')"
      class="portlet box blue-hoki"
    >
      <div
        class="portlet-title bg-blue-chambray"
        @click="toggleCreatePanel()"
      >
        <div class="caption">
          <i class="fa fa-2x fa-plus-circle" />
          Create a Donation Campaign
        </div>
        <div class="tools">
          <a :class="{'expand': !createNewCollapse, 'collapse': createNewCollapse}" />
        </div>
      </div>
      <div
        class="portlet-body relative-block"
        :class="{'display-hide': createNewCollapse}"
      >
        <form
          @submit.prevent="createDonationCampaign()"
        >
          <div class="row">
            <div class="col-md-12">
              <div
                v-show="createErrorMessage.length"
                ref="createErrorMessage"
                class="alert alert-danger"
              >
                <button
                  class="close"
                  @click.prevent="clearError('createErrorMessage')"
                />
                <span>{{ createErrorMessage }}</span>
              </div>
            </div>
            <div
              :class="{
                'col-md-4' : !imagePickerOpen.create,
                'col-md-12' : imagePickerOpen.create
              }"
            >
              <resource-picker
                :image-button="true"
                :image-url="newDonationCampaign.image"
                class="margin-top-15"
                @open="setImagePickerOpen('create', true)"
                @close="setImagePickerOpen('create', false)"
                @selected="setNewImage"
              />
            </div>
            <template v-if="!imagePickerOpen.create">
              <div class="col-md-6">
                <div class="form-group form-md-line-input form-md-floating-label">
                  <input
                    id="form_control_new_name"
                    v-model="newDonationCampaign.name"
                    type="text"
                    class="form-control input-sm"
                    :class="{'edited': newDonationCampaign.name}"
                  >
                  <label for="form_control_new_name">
                    Name
                  </label>
                </div>
                <div class="form-group form-md-line-input form-md-floating-label">
                  <input
                    id="form_control_new_description"
                    v-model="newDonationCampaign.description"
                    type="text"
                    class="form-control input-sm"
                    :class="{'edited': newDonationCampaign.description}"
                  >
                  <label for="form_control_new_description">
                    Description
                  </label>
                </div>
                <div class="form-group form-md-line-input form-md-floating-label">
                  <input
                    id="form_control_new_key"
                    v-model="newDonationCampaign.key"
                    type="text"
                    class="form-control input-sm"
                    :class="{'edited': newDonationCampaign.key}"
                  >
                  <label for="form_control_new_key">
                    Key
                  </label>
                </div>
                <div
                  class="field__wrapper"
                >
                  <label class="field__label--date-picker">
                    Starts on
                  </label>
                  <el-date-picker
                    v-model="newDonationCampaign.start_from"
                    type="date"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    :clearable="false"
                    placeholder="YYYY-MM-DD"
                  />
                </div>
                <div
                  class="field__wrapper"
                >
                  <label class="field__label--date-picker">
                    Ends on
                  </label>
                  <el-date-picker
                    v-model="newDonationCampaign.end_on"
                    type="date"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    :clearable="false"
                    placeholder="YYYY-MM-DD"
                  />
                </div>
              </div>
            </template>
          </div>
          <div class="row mt-1em">
            <div class="col-md-10">
              <button
                type="submit"
                class="btn blue pull-right"
                :disabled="creating"
              >
                Create
                <i
                  v-show="creating"
                  class="fa fa-spinner fa-pulse fa-fw"
                />
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    <!-- END CREATE -->
    <!-- BEGIN LIST -->
    <div
      id="donations-container"
      class="portlet light portlet-fit bordered margin-top-20"
    >
      <div class="portlet-title bg-blue-chambray">
        <div class="menu-image-main">
          <img src="@/../public/client_logo.png">
        </div>
        <div class="caption">
          <span class="caption-subject font-default bold uppercase">
            Donation Campaigns
          </span>
          <div class="caption-desc font-grey-cascade">
            Edit or delete donation campaigns.
          </div>
        </div>
      </div>
      <div class="col-md-12">
        <div
          v-show="listErrorMessage.length"
          ref="listErrorMessage"
          class="alert alert-danger"
        >
          <button
            class="close"
            data-close="alert"
            @click="clearError('listErrorMessage')"
          />
          <span>{{ listErrorMessage }}</span>
        </div>
      </div>
      <div class="portlet-body relative-block">
        <loading-screen
          v-if="loadingDonationCampaigns"
          :show="loadingDonationCampaigns"
          :color="'#2C3E50'"
          :display="'inline'"
        />
        <template v-else>
          <div
            v-if="donationCampaigns.length"
            class="mt-element-list margin-top-15"
          >
            <div class="mt-list-container list-news ext-1 no-border">
              <ul>
                <li
                  v-for="donationCampaign in donationCampaigns"
                  :id="'donation-campaign-' + donationCampaign.id"
                  :key="donationCampaign.id"
                  class="mt-list-item actions-at-left margin-top-15 three-vertical-actions"
                >
                  <div class="list-item-actions">
                    <el-tooltip
                      v-if="can('donation update')"
                      content="Edit"
                      effect="light"
                      placement="right"
                    >
                      <a
                        class="btn btn-circle btn-icon-only btn-default"
                        @click.stop="editDonationCampaign(donationCampaign)"
                      >
                        <i class="fa fa-lg fa-pencil" />
                      </a>
                    </el-tooltip>
                    <el-tooltip
                      v-if="!can('donation update')"
                      content="View"
                      effect="light"
                      placement="right"
                    >
                      <a
                        class="btn btn-circle btn-icon-only btn-default"
                        @click.stop="editDonationCampaign(donationCampaign)"
                      >
                        <i class="fa fa-lg fa-eye" />
                      </a>
                    </el-tooltip>
                    <el-tooltip
                      v-if="can('donation delete')"
                      content="Delete"
                      effect="light"
                      placement="right"
                    >
                      <a
                        class="btn btn-circle btn-icon-only btn-default"
                        @click.stop="openDeleteModal(donationCampaign)"
                      >
                        <i class="fa fa-lg fa-trash" />
                      </a>
                    </el-tooltip>
                  </div>

                  <div class=" row">
                    <div class="col-md-2">
                      <div class="list-thumb">
                        <img
                          :src="donationCampaign.image"
                        >
                      </div>
                    </div>
                    <div class="col-md-10 mb-1em">
                      <div class="list-datetime bold font-red">
                        <span>{{ donationCampaign.name }}</span>
                      </div>
                    </div>
                    <div class="col-md-10 mb-1em">
                      <span>{{ donationCampaign.description }}</span><br>
                    </div>
                    <div
                      class="col-md-10 col-md-offset-2"
                    >
                      <strong>Key </strong>
                      <span>{{ donationCampaign.key }}</span><br>
                    </div>
                    <div
                      class="col-md-5 col-md-offset-2"
                    >
                      <strong>Starts on </strong>
                      <span>{{ donationCampaign.start_from }}</span><br>
                    </div>
                    <div
                      class="col-md-5"
                    >
                      <strong>Ends on </strong>
                      <span>{{ donationCampaign.end_on }}</span><br>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div
            v-else
            class="margin-top-20"
          >
            <no-results
              :show="true"
              :type="'donation campaigns'"
            />
          </div>
        </template>
      </div>
    </div>
    <!-- END LIST -->

    <!-- START EDIT -->
    <modal
      ref="editModal"
      :show="showEditModal"
      effect="fade"
      @closeOnEscape="closeEditModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeEditModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Edit Donation Campaign
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div class="row">
          <div class="col-md-12">
            <div
              v-show="editErrorMessage"
              ref="editErrorMessage"
              class="alert alert-danger"
            >
              <button
                class="close"
                data-close="alert"
                @click.prevent="clearError('editErrorMessage')"
              />
              <span>{{ editErrorMessage }}</span>
            </div>
          </div>
        </div>
        <form>
          <div class="row">
            <div class="col-md-12">
              <div
                v-show="createErrorMessage.length"
                ref="createErrorMessage"
                class="alert alert-danger"
              >
                <button
                  class="close"
                  @click.prevent="clearError('createErrorMessage')"
                />
                <span>{{ createErrorMessage }}</span>
              </div>
            </div>
            <div
              class="col-md-12"
            >
              <resource-picker
                :image-button="true"
                :image-url="donationCampaignToEdit.image"
                class="margin-top-15"
                @open="setImagePickerOpen('edit', true)"
                @close="setImagePickerOpen('edit', false)"
                @selected="setEditedImage"
              />
            </div>
            <template v-if="!imagePickerOpen.edit">
              <div class="col-md-12">
                <div class="form-group form-md-line-input form-md-floating-label">
                  <input
                    id="form_control_new_name"
                    v-model="donationCampaignToEdit.name"
                    type="text"
                    class="form-control input-sm"
                    :class="{'edited': donationCampaignToEdit.name}"
                  >
                  <label for="form_control_new_name">
                    Name
                  </label>
                </div>
                <div class="form-group form-md-line-input form-md-floating-label">
                  <input
                    id="form_control_new_description"
                    v-model="donationCampaignToEdit.description"
                    type="text"
                    class="form-control input-sm"
                    :class="{'edited': donationCampaignToEdit.description}"
                  >
                  <label for="form_control_new_description">
                    Description
                  </label>
                </div>
                <div class="form-group form-md-line-input form-md-floating-label">
                  <input
                    id="form_control_new_key"
                    v-model="donationCampaignToEdit.key"
                    type="text"
                    class="form-control input-sm"
                    :class="{'edited': donationCampaignToEdit.key}"
                  >
                  <label for="form_control_new_key">
                    Key
                  </label>
                </div>
                <div
                  class="field__wrapper"
                >
                  <label class="field__label--date-picker">
                    Starts on
                  </label>
                  <el-date-picker
                    v-model="donationCampaignToEdit.start_from"
                    type="date"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    :clearable="false"
                    placeholder=""
                  />
                </div>
                <div
                  class="field__wrapper"
                >
                  <label class="field__label--date-picker">
                    Ends on
                  </label>
                  <el-date-picker
                    v-model="donationCampaignToEdit.end_on"
                    type="date"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    :clearable="false"
                    placeholder=""
                  />
                </div>
              </div>
            </template>
          </div>
        </form>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer clear"
      >
        <button
          v-if="!can('donation update')"
          type="button"
          class="btn blue"
          @click="closeEditModal()"
        >
          Close
        </button>
        <button
          v-else
          type="submit"
          class="btn blue"
          :disabled="updating"
          @click="updateDonationCampaign()"
        >
          Save
          <i
            v-show="updating"
            class="fa fa-spinner fa-pulse fa-fw"
          />
        </button>
      </div>
    </modal>
    <!-- END EDIT -->

    <!-- START DELETE -->
    <modal
      ref="deleteModal"
      :show="showDeleteModal"
      effect="fade"
      @closeOnEscape="closeDeleteModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeDeleteModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Confirm Delete
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div class="row">
          <div class="col-md-12">
            <div
              v-show="deleteErrorMessage"
              ref="deleteErrorMessage"
              class="alert alert-danger"
            >
              <button
                class="close"
                data-close="alert"
                @click.prevent="clearError('deleteErrorMessage')"
              />
              <span>{{ deleteErrorMessage }}</span>
            </div>
          </div>
        </div>
        <p>Are you sure you want to delete {{ donationCampaignToDelete.name }}?</p>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer clear"
      >
        <button
          type="button"
          class="btn blue"
          :disabled="deleting"
          @click="deleteDonationCampaign()"
        >
          Delete
          <i
            v-show="deleting"
            class="fa fa-spinner fa-pulse fa-fw"
          />
        </button>
      </div>
    </modal>
    <!-- END DELETE -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import LoadingScreen from '@/components/modules/LoadingScreen'
import Modal from '@/components/modules/Modal'
import NoResults from '@/components/modules/NoResults'
import DonationsFunctions from '@/controllers/Donations'
import ajaxErrorHandler from '@/controllers/ErrorController'
import ResourcePicker from '@/components/modules/ResourcePicker'

export default {
  name: 'DonationCampaigns',
  components: {
    LoadingScreen,
    Modal,
    NoResults,
    ResourcePicker
  },
  data () {
    return {
      createNewCollapse: true,
      imagePickerOpen: {
        create: false,
        edit: false
      },
      creating: false,
      createErrorMessage: '',
      newDonationCampaign: {
        name: '',
        key: '',
        start_from: '',
        end_on: '',
        description: '',
        image: ''
      },

      loadingDonationCampaigns: false,
      listErrorMessage: '',
      donationCampaigns: [],

      showEditModal: false,
      editErrorMessage: '',
      updating: false,
      donationCampaignToEdit: {
        name: '',
        key: '',
        start_from: '',
        end_on: '',
        description: '',
        image: ''
      },

      showDeleteModal: false,
      deleting: false,
      deleteErrorMessage: '',
      donationCampaignToDelete: {
        name: '',
        key: '',
        start_from: '',
        end_on: '',
        description: '',
        image: ''
      }
    }
  },
  computed: {
    ...mapGetters(['can'])
  },
  created () {
    this.getDonationCampaigns()
  },
  methods: {
    /**
     * To toggle between the open and closed state of the resource picker
     * @function
     * @param {string} object - The view the image is for
     * @param {object} value - The open / closed value of the picker
     * @returns {undefined}
     */
    setImagePickerOpen (object, value) {
      this.imagePickerOpen[object] = value
    },
    /**
     * To set selected image URL
     * @function
     * @param {object} value - The selected image
     * @returns {undefined}
     */
    setNewImage (image) {
      this.newDonationCampaign.image = image.image_url
    },
    /**
     * To set selected image URL
     * @function
     * @param {object} value - The selected image
     * @returns {undefined}
     */
    setEditedImage (image) {
      this.donationCampaignToEdit.image = image.image_url
    },
    /**
     * To toggle the create panel
     * @function
     * @returns {undefined}
     */
    toggleCreatePanel () {
      this.createNewCollapse = !this.createNewCollapse
    },
    /**
     * To clear the current error.
     * @function
     * @param {object} errorMessageName - The error message to be cleared.
     * @returns {undefined}
     */
    clearError (errorMessageName) {
      this[errorMessageName] = ''
    },
    /**
     * To check if the menu data is valid before submitting to the backend.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    validateNew () {
      var _this = this
      return new Promise(function (resolve, reject) {
        if (!_this.newDonationCampaign.image) {
          reject('Please select an image')
        } else if (!_this.newDonationCampaign.name) {
          reject('Name cannot be blank')
        } else if (!_this.newDonationCampaign.description) {
          reject('Description cannot be blank')
        } else if (!_this.newDonationCampaign.key) {
          reject('Key cannot be blank')
        } else if (!_this.newDonationCampaign.start_from) {
          reject('Start date cannot be blank')
        } else if (!_this.newDonationCampaign.end_on) {
          reject('End date cannot be blank')
        } else if (_this.newDonationCampaign.end_on < _this.newDonationCampaign.start_from) {
          reject('End date cannot be before start date')
        }
        resolve('Hurray')
      })
    },
    /**
     * To create a new donation campaign.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    createDonationCampaign () {
      var _this = this
      _this.clearError('createErrorMessage')

      this.validateNew()
        .then(() => {
          this.creating = true
          DonationsFunctions.createDonationCampaign(this.newDonationCampaign)
            .then(response => {
              _this.showCreateSuccess(response.payload)
              _this.clearNew()
              _this.getDonationCampaigns()
            })
            .catch(reason => {
              ajaxErrorHandler({
                reason,
                errorText: 'We could not create the Donation Campaign',
                errorName: 'createErrorMessage',
                vue: _this
              })
            })
            .finally(() => {
              _this.creating = false
            })
        })
        .catch(reason => {
          this.createErrorMessage = reason
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showCreateSuccess (payload = {}) {
      if (payload === null) payload = {}
      let title = 'Success'
      let text = 'The Donation Campaign has been created'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The Donation Campaign has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To clear the new donation campaign form.
     * @function
     * @returns {undefined}
     */
    clearNew () {
      this.newDonationCampaign = {
        name: '',
        key: '',
        start_from: '',
        end_on: '',
        description: '',
        image: ''
      }
    },
    /**
     * To get a list of all donation campaigns.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getDonationCampaigns () {
      this.loadingDonationCampaigns = true
      this.donationCampaigns = []
      var _this = this
      return DonationsFunctions.getDonationCampaigns()
        .then(response => {
          _this.donationCampaigns = response.payload.sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1
            } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1
            } else {
              return 0
            }
          })
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not fetch Donation Campaigns',
            errorName: 'listErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          _this.loadingDonationCampaigns = false
        })
    },
    /**
     * To show the edit modal
     * @function
     * @param {object} donationCampaign - The selected donation campaign.
     * @returns {undefined}
     */
    editDonationCampaign (donationCampaign) {
      this.donationCampaignToEdit = { ...donationCampaign }
      this.showEditModal = true
    },
    /**
     * To check if the menu data is valid before submitting to the backend.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    validateEdited () {
      var _this = this
      return new Promise(function (resolve, reject) {
        if (!_this.donationCampaignToEdit.image) {
          reject('Please select an image')
        } else if (!_this.donationCampaignToEdit.name) {
          reject('Name cannot be blank')
        } else if (!_this.donationCampaignToEdit.description) {
          reject('Description cannot be blank')
        } else if (!_this.donationCampaignToEdit.key) {
          reject('Key cannot be blank')
        } else if (!_this.donationCampaignToEdit.start_from) {
          reject('Start date cannot be blank')
        } else if (!_this.donationCampaignToEdit.end_on) {
          reject('End date cannot be blank')
        } else if (_this.donationCampaignToEdit.end_on < _this.donationCampaignToEdit.start_from) {
          reject('End date cannot be before start date')
        }
        resolve('Hurray')
      })
    },
    /**
     * To update a donation campaign.
     * @function
     * @returns {object} A promise that will validate the input form
     */
    updateDonationCampaign () {
      var _this = this
      _this.clearError('editErrorMessage')

      let payload = {
        ...this.donationCampaignToEdit,
        start_from: this.donationCampaignToEdit.start_from.substr(0, 10),
        end_on: this.donationCampaignToEdit.end_on.substr(0, 10)
      }

      this.validateEdited()
        .then(() => {
          this.updating = true
          DonationsFunctions.updateDonationCampaign(payload)
            .then(response => {
              _this.getDonationCampaigns()
              _this.closeEditModal()
              _this.showEditSuccess(response.payload)
              _this.resetEdit()
            })
            .catch(reason => {
              ajaxErrorHandler({
                reason,
                errorText: 'We could not update the Donation Campaign',
                errorName: 'editErrorMessage',
                vue: _this,
                containerRef: 'editModal'
              })
            })
            .finally(() => {
              _this.updating = false
            })
        })
        .catch(reason => {
          this.editErrorMessage = reason
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showEditSuccess (payload = {}) {
      if (payload === null) payload = {}
      let title = 'Success'
      let text = 'The Donation Campaign has been saved'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The changes have been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To close the edit modal.
     * @function
     * @returns {undefined}
     */
    closeEditModal () {
      this.showEditModal = false
    },
    /**
     * To reset the edit form
     * @function
     * @returns {undefined}
     */
    resetEdit () {
      this.donationCampaignToEdit = {
        name: '',
        key: '',
        start_from: '',
        end_on: '',
        description: '',
        image: ''
      }
    },
    /**
     * To display the modal for deleting an donation campaign.
     * @function
     * @param {object} donationCampaign - The selected donation campaign
     * @returns {undefined}
     */
    openDeleteModal (donationCampaign) {
      this.donationCampaignToDelete = { ...donationCampaign }
      this.showDeleteModal = true
    },
    /**
     * To delete a donation campaign
     * @function
     * @returns {undefined}
     */
    deleteDonationCampaign () {
      this.deleting = true
      var _this = this
      DonationsFunctions.deleteDonationCampaign(_this.donationCampaignToDelete.id)
        .then(response => {
          _this.getDonationCampaigns()
          _this.closeDeleteModal()
          _this.showDeleteSuccess(response.payload)
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: `We could not delete ${this.donationCampaignToDelete.name}`,
            errorName: 'deleteErrorMessage',
            vue: _this,
            containerRef: 'deleteModal'
          })
        })
        .finally(() => {
          _this.deleting = false
        })
    },
    /**
     * To notify user of the outcome of the call
     * @function
     * @param {object} payload - The payload object from the server response
     * @returns {undefined}
     */
    showDeleteSuccess (payload = {}) {
      if (payload === null) payload = {}
      let title = 'Success'
      let text = 'The Discount has been deleted'
      let type = 'success'

      if (payload && payload.pending_approval) {
        title = 'Approval Required'
        text = 'The removal has been sent for approval'
        type = 'info'
      }

      this.$swal({
        title,
        text,
        type
      })
    },
    /**
     * To close the delete modal.
     * @function
     * @returns {undefined}
     */
    closeDeleteModal () {
      this.showDeleteModal = false
    }
  }
}
</script>

<style scoped>
.mt-element-list .list-news.ext-1.mt-list-container ul > .mt-list-item:hover {
  background-color: white;
}
.two-vertical-actions {
  min-height: 90px;
}
.field__label--date-picker{
  margin: 10px 0 5px 0;
  display: block;
  font-size: 14px;
  color: #999;
}
</style>
